import React, { useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { PatientDataContext } from '../context/PatientDataContext';
import ReviewSessionTracker from '../analytics/ReviewSessionTracker';

/**
 * Loading view shown while the requested patient's data is being fetched.
 *
 * When the clinician arrives from the dashboard table we receive a lightweight
 * `patientSummary` ({ firstName, lastName }) via React Router location state,
 * so we can greet them with the CORRECT incoming patient's name immediately —
 * never the previous patient's. On a bookmark / direct-URL load there is no
 * route state, so we fall back to a generic message.
 *
 * Note: this name comes purely from navigation state and is never written into
 * patientDataModel, so it cannot leak into any component that reads the model.
 */
function PatientLoading({ summary }) {
  const name = summary && (summary.firstName || summary.lastName)
    ? `${summary.firstName || ''} ${summary.lastName || ''}`.trim()
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700 mx-auto"></div>
        <p className="mt-4 text-gray-600">
          {name ? `Loading ${name}…` : 'Loading patient…'}
        </p>
      </div>
    </div>
  );
}

/**
 * PatientRouteWrapper
 * -------------------
 * Wraps every `/patient/:patientId/*` route. It is the single choke point that
 * decides whether the patient-specific page below it is allowed to render.
 *
 * THE BUG THIS GUARDS AGAINST:
 * PatientDataProvider is mounted once at the app root and never unmounts, so
 * `patientDataModel` retains the PREVIOUS patient's data when the clinician
 * switches patients. The old guard here only checked "is there any data?",
 * which is true with the previous patient's data — so the page rendered the
 * wrong patient (wrong name, etc.) for the 10-15s it took the new fetch to
 * complete.
 *
 * THE FIX:
 * Render the children only when the data currently in the context actually
 * belongs to the patient in the URL — i.e. `loadedPatientId === patientId`.
 * Until that is true we show a loading state. This single declarative check
 * fixes BOTH navigation paths:
 *   - Dashboard → patient: loadedPatientId still points at the previous patient
 *     (or the cold-load companion key), so we show loading, not stale data.
 *   - Bookmark / direct URL: same logic; loading until the fetch confirms.
 *
 * It deliberately does NOT touch the write queue, so there is no risk of losing
 * or cross-contaminating pending edits.
 *
 * `LoadingComponent` (optional): a route may supply a page-shaped skeleton to
 * render while loading (e.g. PatientOverviewSkeleton) instead of the default
 * centred spinner. It receives the same `summary` (incoming patient name from
 * navigation state) so it can show the correct name immediately.
 */
export default function PatientRouteWrapper({ children, LoadingComponent }) {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    patientDataModel,
    loadedPatientId,
    fetchPatientData,
    flushPendingUpdates,
    error
  } = useContext(PatientDataContext);

  useEffect(() => {
    if (!patientId) return;
    // Wait for any in-flight writes to commit before reading from
    // Firestore.  Without this, the fetch can read stale data that
    // predates the queued writes (e.g. symptom edits on the previous
    // page), and its setPatientDataModel call can overwrite the fresh
    // data that the flush transaction's own re-read would have set.
    //
    // flushPendingUpdates fast-paths to Promise.resolve() when nothing
    // is pending, so this adds zero latency on normal navigation.
    const loadFreshData = async () => {
      await flushPendingUpdates();
      fetchPatientData(patientId);
    };
    loadFreshData();
  }, [fetchPatientData, flushPendingUpdates, patientId]);

  if (error) {
    return null; // PatientDataContext will handle the redirect
  }

  // Only render the patient page when the loaded data is for THIS patient.
  // Note: when navigating between sections of the SAME patient, patientId is
  // unchanged and loadedPatientId already matches, so there is no loading flash.
  const isDataForThisPatient = patientDataModel && loadedPatientId === patientId;
  if (!isDataForThisPatient) {
    const summary = location.state?.patientSummary;
    // Prefer a route-supplied page-shaped skeleton; fall back to the spinner.
    if (LoadingComponent) {
      return <LoadingComponent summary={summary} />;
    }
    return <PatientLoading summary={summary} />;
  }

  // Module guard: if this patient uses a subcollection-based module (e.g. Autism),
  // redirect to the correct modular route rather than rendering ADHD-specific pages.
  const patientModule = patientDataModel?.assessmentModule || 'adultAdhd';
  if (patientModule !== 'adultAdhd') {
    const moduleRouteMap = { adultAutism: 'adult-autism', cypAdhd: 'cyp-adhd' };
    const moduleSlug = moduleRouteMap[patientModule] || patientModule;
    navigate(`/patient/${patientId}/${moduleSlug}`, { replace: true });
    return null;
  }

  // Renders nothing; measures review sittings on the report review routes.
  // Mounted here, after the data-ownership gate, so a sitting can only start
  // once the page below is genuinely showing this patient.
  return (
    <>
      {children}
      <ReviewSessionTracker />
    </>
  );
}
